import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { DictionaryEntry } from '../services/api';

const entrySchema = z.object({
  origin_url: z.string().optional(),
  who: z.string().optional(),
  source_name: z.string().min(1, 'Source name is required'),
  group_name: z.string().min(1, 'Group name is required'),
  group_id: z.number().int().positive('Group ID must be a positive integer'),
  field_name: z.string().min(1, 'Field name is required'),
  lookup_field_name: z.string().optional(),
  app_label: z.string().optional(),
  record_order: z.number().int().positive().optional(),
  column_order: z.number().int().positive().optional(),
  comments: z.string().optional(),
  obscure: z.string().optional(),
  conversion: z.string().optional(),
  process_dataset: z.number().int().default(1),
  is_column_in_source: z.number().int().default(1),
  push_lp: z.number().int().default(1),
  is_required: z.number().int().default(0),
  is_ready: z.number().int().default(1),
  is_key: z.number().int().default(0),
  field_format: z.string().optional(),
  sql_data_type: z.string().default('VARCHAR'),
  sql_max_length: z.number().int().default(4000),
});

type EntryFormData = z.infer<typeof entrySchema>;

interface DictionaryFormProps {
  entry?: DictionaryEntry;
  onSubmit: (data: Partial<DictionaryEntry>) => Promise<void>;
  onCancel: () => void;
}

export default function DictionaryForm({ entry, onSubmit, onCancel }: DictionaryFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EntryFormData>({
    resolver: zodResolver(entrySchema),
    defaultValues: entry || {
      process_dataset: 1,
      is_column_in_source: 1,
      push_lp: 1,
      is_required: 0,
      is_ready: 1,
      is_key: 0,
      sql_data_type: 'VARCHAR',
      sql_max_length: 4000,
    },
  });

  const onSubmitForm = async (data: EntryFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="card space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{entry ? 'Edit Entry' : 'Create Entry'}</h2>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Source Name *
          </label>
          <input {...register('source_name')} className="input" />
          {errors.source_name && (
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.source_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Group Name *
          </label>
          <input {...register('group_name')} className="input" />
          {errors.group_name && (
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.group_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Group ID *
          </label>
          <input
            type="number"
            {...register('group_id', { valueAsNumber: true })}
            className="input"
          />
          {errors.group_id && (
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.group_id.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Field Name *
          </label>
          <input {...register('field_name')} className="input" />
          {errors.field_name && (
            <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.field_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            App Label
          </label>
          <input {...register('app_label')} className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            SQL Data Type
          </label>
          <select {...register('sql_data_type')} className="input">
            <option value="VARCHAR">VARCHAR</option>
            <option value="INTEGER">INTEGER</option>
            <option value="DECIMAL">DECIMAL</option>
            <option value="DATE">DATE</option>
            <option value="DATETIME">DATETIME</option>
            <option value="BOOLEAN">BOOLEAN</option>
            <option value="TEXT">TEXT</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            SQL Max Length
          </label>
          <input
            type="number"
            {...register('sql_max_length', { valueAsNumber: true })}
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Lookup Field Name
          </label>
          <input {...register('lookup_field_name')} className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Record Order
          </label>
          <input
            type="number"
            {...register('record_order', { valueAsNumber: true })}
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Column Order
          </label>
          <input
            type="number"
            {...register('column_order', { valueAsNumber: true })}
            className="input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Comments
          </label>
          <textarea {...register('comments')} className="input" rows={3} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Obscure Function
          </label>
          <input {...register('obscure')} className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Conversion Function
          </label>
          <input {...register('conversion')} className="input" />
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('is_required', { valueAsNumber: true })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('is_ready', { valueAsNumber: true })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Ready</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('is_key', { valueAsNumber: true })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Is Key</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('process_dataset', { valueAsNumber: true })}
            className="mr-2"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Process Dataset</span>
        </label>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : entry ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

